import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../utils/api';

const STATUS_COLORS = {
  New: 'bg-gray-500/20 text-gray-400',
  Emailed: 'bg-blue-500/20 text-blue-400',
  'Follow Up': 'bg-orange-500/20 text-orange-400',
  Replied: 'bg-purple-500/20 text-purple-400',
  Booked: 'bg-green-500/20 text-green-400',
  Closed: 'bg-emerald-700/20 text-emerald-400',
  Cold: 'bg-gray-400/10 text-gray-500',
  'Opted Out': 'bg-red-500/10 text-red-400',
  'Active Client': 'bg-emerald-500/20 text-emerald-300',
};

const SERVICES = [
  'Google Review Agent',
  'Website Development',
  'Chatbot (Carl)',
  'Automated Admin Assistant',
];

const STATUSES = ['New', 'Emailed', 'Follow Up', 'Replied', 'Booked', 'Closed', 'Cold', 'Opted Out', 'Active Client'];

const emptyForm = {
  businessName: '', ownerName: '', email: '', phone: '',
  suburb: '', city: '', industry: '', serviceGap: '', notes: ''
};

// Custom Email Modal
function CustomEmailModal({ prospect, onClose, onSent }) {
  const [subject, setSubject] = useState(api.getCorrectSubject(prospect.serviceGap));
  const [context, setContext] = useState(
    `Business: ${prospect.businessName}\nIndustry: ${prospect.industry || ''}\nService: ${prospect.serviceGap || ''}\nNotes: ${prospect.notes || ''}`
  );
  const [body, setBody] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const result = await api.generateCustomEmail(prospect.id, context);
      setBody(result.body);
      setSubject(result.subject);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleSend = async () => {
    if (!body.trim()) { setError('Generate an email first'); return; }
    setSending(true);
    setError('');
    try {
      await api.sendEmail(prospect.id, subject, body);
      setSuccess('Email sent!');
      setTimeout(() => { onSent(); onClose(); }, 1500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Custom Email — {prospect.businessName}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl leading-none">&times;</button>
        </div>

        {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-sm text-red-400">{error}</div>}
        {success && <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-4 text-sm text-green-400">{success}</div>}

        <div className="mb-3">
          <label className="block text-xs text-gray-400 mb-1">Subject Line</label>
          <input
            type="text"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
          />
        </div>

        <div className="mb-3">
          <label className="block text-xs text-gray-400 mb-1">Context / Custom Instructions (given to AI)</label>
          <textarea
            value={context}
            onChange={e => setContext(e.target.value)}
            rows={4}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
            placeholder="Add any extra context or instructions for the AI..."
          />
        </div>

        <button
          onClick={handleGenerate}
          disabled={generating}
          className="mb-4 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          {generating ? 'Generating...' : 'Generate with AI'}
        </button>

        {body && (
          <div className="mb-4">
            <label className="block text-xs text-gray-400 mb-1">Email Body</label>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={12}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
            />
          </div>
        )}

        <div className="flex gap-3">
          {body && (
            <button
              onClick={handleSend}
              disabled={sending}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              {sending ? 'Sending...' : 'Send Email'}
            </button>
          )}
          <button onClick={onClose} className="bg-gray-700 hover:bg-gray-600 text-white px-5 py-2 rounded-lg text-sm transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Prospects() {
  const [prospects, setProspects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterService, setFilterService] = useState('');
  const [error, setError] = useState('');
  const [customEmailProspect, setCustomEmailProspect] = useState(null);

  const load = useCallback(() => {
    api.getProspects().then(data => setProspects(Array.isArray(data) ? data : [])).catch(e => setError(e.message));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    try {
      if (editing) {
        await api.updateProspect(editing, form);
      } else {
        await api.addProspect(form);
      }
      setShowForm(false);
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleEdit = (p) => {
    setEditing(p.id);
    setForm({
      businessName: p.businessName, ownerName: p.ownerName, email: p.email,
      phone: p.phone, suburb: p.suburb, city: p.city, industry: p.industry,
      serviceGap: p.serviceGap, notes: p.notes, status: p.status
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this prospect?')) return;
    await api.deleteProspect(id);
    load();
  };

  const handleOptOut = async (p) => {
    if (!window.confirm(`Remove ${p.businessName} from outreach? They will be marked as Opted Out.`)) return;
    await api.updateProspect(p.id, { status: 'Opted Out' });
    load();
  };

  const handleStatusChange = async (id, status) => {
    await api.updateProspect(id, { status });
    load();
  };

  const handleToggleReplied = async (p) => {
    await api.updateProspect(p.id, { replied: !p.replied });
    load();
  };

  const handleToggleCalled = async (p) => {
    try {
      await api.updateProspect(p.id, { called: !p.called });
      load();
    } catch (e) {
      // called column may not exist yet — fail silently
    }
  };

  const filtered = prospects.filter(p => {
    if (search && !p.businessName.toLowerCase().includes(search.toLowerCase()) && !(p.ownerName || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (filterStatus && p.status !== filterStatus) return false;
    if (filterService && p.serviceGap !== filterService) return false;
    return true;
  });

  const formField = (label, key, type = 'text') => (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      {type === 'textarea' ? (
        <textarea
          value={form[key] || ''}
          onChange={e => setForm({ ...form, [key]: e.target.value })}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
          rows={3}
        />
      ) : type === 'select-service' ? (
        <select
          value={form[key] || ''}
          onChange={e => setForm({ ...form, [key]: e.target.value })}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
        >
          <option value="">Select...</option>
          {SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      ) : type === 'select-status' ? (
        <select
          value={form[key] || 'New'}
          onChange={e => setForm({ ...form, [key]: e.target.value })}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
        >
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      ) : (
        <input
          type={type}
          value={form[key] || ''}
          onChange={e => setForm({ ...form, [key]: e.target.value })}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
        />
      )}
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Prospects</h2>
        <button
          onClick={() => { setShowForm(true); setEditing(null); setForm(emptyForm); }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          + Add Prospect
        </button>
      </div>

      {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">{editing ? 'Edit Prospect' : 'Add Prospect'}</h3>
            <div className="grid grid-cols-2 gap-4">
              {formField('Business Name', 'businessName')}
              {formField('Owner Name', 'ownerName')}
              {formField('Email', 'email', 'email')}
              {formField('Phone', 'phone', 'tel')}
              {formField('Suburb', 'suburb')}
              {formField('City', 'city')}
              {formField('Industry', 'industry')}
              {formField('Service Gap', 'serviceGap', 'select-service')}
              {editing && formField('Status', 'status', 'select-status')}
            </div>
            <div className="mt-4">{formField('Notes', 'notes', 'textarea')}</div>
            <div className="flex gap-3 mt-5">
              <button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-medium">
                {editing ? 'Update' : 'Add'}
              </button>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="bg-gray-700 hover:bg-gray-600 text-white px-5 py-2 rounded-lg text-sm">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Email Modal */}
      {customEmailProspect && (
        <CustomEmailModal
          prospect={customEmailProspect}
          onClose={() => setCustomEmailProspect(null)}
          onSent={load}
        />
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="text"
          placeholder="Search prospects..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 w-64"
        />
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
        >
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={filterService}
          onChange={e => setFilterService(e.target.value)}
          className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
        >
          <option value="">All Services</option>
          {SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="text-sm text-gray-500 self-center">{filtered.length} prospects</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-800">
        <table className="w-full text-sm">
          <thead className="bg-gray-900">
            <tr className="text-left text-gray-400">
              <th className="px-4 py-3 font-medium">Business</th>
              <th className="px-4 py-3 font-medium">Contact</th>
              <th className="px-4 py-3 font-medium">Service Gap</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Replied</th>
              <th className="px-4 py-3 font-medium">Called</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {filtered.map(p => (
              <tr
                key={p.id}
                className={`hover:bg-gray-900/50 ${p.status === 'Opted Out' ? 'opacity-40' : ''}`}
              >
                <td className="px-4 py-3">
                  <p className="font-medium">{p.businessName}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{p.suburb}{p.city ? `, ${p.city}` : ''}</p>
                </td>
                <td className="px-4 py-3">
                  <p className="text-gray-300 text-xs">{p.ownerName || '—'}</p>
                  <p className="text-gray-500 text-xs">{p.email || '—'}</p>
                  {p.phone && <p className="text-gray-500 text-xs">{p.phone}</p>}
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs px-2 py-1 rounded-full bg-blue-500/10 text-blue-400">{p.serviceGap || '—'}</span>
                </td>
                <td className="px-4 py-3">
                  <select
                    value={p.status}
                    onChange={e => handleStatusChange(p.id, e.target.value)}
                    className={`text-xs px-2 py-1 rounded-full border-0 cursor-pointer ${STATUS_COLORS[p.status] || ''}`}
                    style={{ background: 'transparent' }}
                  >
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleToggleReplied(p)}
                    title={p.replied ? 'Mark as not replied' : 'Mark as replied'}
                    className={`text-xs px-2 py-1 rounded-lg transition-colors ${
                      p.replied
                        ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
                        : 'bg-gray-800 text-gray-500 hover:bg-gray-700 hover:text-gray-300'
                    }`}
                  >
                    {p.replied ? 'Replied' : 'No'}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleToggleCalled(p)}
                    title={p.called ? 'Mark as not called' : 'Mark as called'}
                    className={`text-xs px-2.5 py-1.5 rounded-lg transition-colors ${
                      p.called
                        ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                        : 'text-gray-600 hover:text-gray-400 hover:bg-gray-800'
                    }`}
                  >
                    {p.called ? (
                      <span>&#9742;</span>
                    ) : (
                      <span className="text-gray-600">&#9742;</span>
                    )}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 flex-wrap">
                    <button
                      onClick={() => setCustomEmailProspect(p)}
                      className="text-xs px-2 py-1.5 bg-indigo-600/20 text-indigo-400 rounded-lg hover:bg-indigo-600/30 transition-colors"
                    >
                      Custom
                    </button>
                    <button
                      onClick={() => handleEdit(p)}
                      className="text-xs px-2 py-1.5 text-gray-400 hover:text-white transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleOptOut(p)}
                      title="Remove from outreach"
                      className="text-xs px-2 py-1.5 text-gray-500 hover:text-orange-400 transition-colors"
                    >
                      Remove
                    </button>
                    <button
                      onClick={() => handleDelete(p.id)}
                      className="text-xs px-2 py-1.5 text-gray-500 hover:text-red-400 transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No prospects yet. Add manually or push from Leads.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
